'use client';

import { useTransition } from 'react';
import Link from 'next/link';
import * as Sentry from '@sentry/nextjs';
import { MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { analytics } from '@kit/analytics';
import { getOrCreateDirectConversationAction } from '@kit/chat/actions';
import { Button } from '@kit/ui/button';
import { toast } from '@kit/ui/sonner';
import { cn } from '@kit/ui/utils';

import { useFloatingChat } from '~/components/floating-chat';
import { linkConversationListingAction } from '../../_lib/server/link-conversation-listing.action';

type ButtonVariant = React.ComponentProps<typeof Button>['variant'];
type ButtonSize = React.ComponentProps<typeof Button>['size'];

interface MessageSellerButtonProps {
  /** profiles.id of the seller to open a direct conversation with. */
  sellerId: string;
  /** Listing this thread is about — recorded against the conversation so the
   *  seller's mark-as-sold picker and the inbox item card can find it. */
  listingId: string;
  /** Seller display name — shown as the chat panel title. */
  sellerName: string;
  /** Seller profile photo — shown as the chat panel header avatar. */
  sellerAvatarUrl?: string | null;
  /** Listing title — shown under the seller name so the buyer can tell
   *  which item the thread is about. */
  listingTitle?: string;
  /** When false, the button is a sign-in link instead (messaging needs auth). */
  isAuthenticated: boolean;
  /**
   * True when the viewer IS the seller (their own listing). A direct
   * conversation with yourself is impossible, so the button self-hides rather
   * than letting the user tap a control that can only fail (BAL-305).
   */
  isOwnListing?: boolean;
  /** Where to send an anonymous visitor to sign in. */
  signInHref: string;
  label?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
  /** Fired when the user activates the button (e.g. for analytics). */
  onActivate?: () => void;
}

/**
 * Opens a direct conversation with the seller in the existing floating chat
 * bubble (the same widget that hosts the Ballee AI assistant) — NOT a new
 * full page. Reuses `getOrCreateDirectConversationAction` for the conversation
 * and `useFloatingChat().openConversation` to surface it as a transient,
 * item-anchored override (the AI assistant stays what the bubble's button
 * reopens). Anonymous visitors get a sign-in link instead.
 */
export function MessageSellerButton({
  sellerId,
  listingId,
  sellerName,
  sellerAvatarUrl,
  listingTitle,
  isAuthenticated,
  isOwnListing = false,
  signInHref,
  label,
  variant = 'default',
  size,
  fullWidth = false,
  className,
  onActivate,
}: MessageSellerButtonProps) {
  const { t } = useTranslation();
  const { openConversation } = useFloatingChat();
  const [isPending, startTransition] = useTransition();

  const resolvedLabel = label ?? t('explore:messageSeller.label');

  // The seller can't message themselves — don't render a dead control.
  if (isOwnListing) {
    return null;
  }

  if (!isAuthenticated) {
    return (
      <Button
        asChild
        variant={variant}
        size={size}
        className={cn(fullWidth && 'w-full', className)}
      >
        <Link href={signInHref} onClick={() => onActivate?.()}>
          <MessageCircle className="mr-1.5 size-4" />
          {resolvedLabel}
        </Link>
      </Button>
    );
  }

  // A buyer-facing failure of this conversion action (open a DM with the
  // seller) used to surface ONLY as a transient toast — invisible to
  // monitoring, so a stakeholder report of "nothing happens" could not be
  // triaged against telemetry (BAL-322). Record every failure to Sentry +
  // PostHog before the toast so any real occurrence is observable.
  function reportFailure(reason: 'action-failed' | 'threw', error?: unknown) {
    Sentry.captureMessage('message-seller open failed', {
      level: 'error',
      extra: { sellerId, listingId, reason, error },
    });
    void analytics.trackEvent('message-seller-failed', {
      listing_id: listingId,
      seller_id: sellerId,
      reason,
    });
    toast.error(t('explore:messageSeller.openError'));
  }

  function handleClick() {
    onActivate?.();
    startTransition(async () => {
      try {
        const result = await getOrCreateDirectConversationAction(sellerId);
        if (result?.success && result.conversationId) {
          // Record the listing↔conversation link (best-effort; never blocks
          // the chat from opening). Powers mark-as-sold + the inbox item card.
          void linkConversationListingAction({
            conversationId: result.conversationId,
            listingId,
          });
          openConversation(result.conversationId, {
            title: sellerName,
            subtitle: listingTitle,
            avatarUrl: sellerAvatarUrl,
          });
        } else {
          reportFailure('action-failed');
        }
      } catch (error) {
        reportFailure('threw', error);
      }
    });
  }

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      disabled={isPending}
      onClick={handleClick}
      className={cn(fullWidth && 'w-full', className)}
    >
      <MessageCircle className="mr-1.5 size-4" />
      {isPending ? t('explore:messageSeller.opening') : resolvedLabel}
    </Button>
  );
}
